import queryString from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import {
  saveLeadResponse,
  saveUpcomingLeadInfo,
  softDeleteUpcomingLeadInfo
} from "../services/api";
import {
  isValidPhoneNumber,
  normalizeNumber,
  removeWhiteSpaces
} from "../services/utils";
import {
  updateLeadId,
  updateUrlParams,
  updateIsFormSubmitted,
  updateRecordActionState,
  updateReminderEmailFlag
} from "../store/slices/funnelInfo";
import { INSIGHTS_TYPE } from "../templates/strings";
import { generatePolicyVersion } from "../services/helper";
import { trackTikTokSubmitForm } from "../services/tiktokTracking";
import {
  buildTrackingData,
  getLeadGoogleAds,
  metaAdDetailsFromParams
} from "../services/googleAdsAttribution";
import { SuggestionText } from "../components/ContactInfoForm/ContactInfoForm.styles";

// Lead data for step 2 of a two-step funnel opened from the reminder email
// (?leadId=…, maybe on another device): everything comes from the upcoming
// lead of step 1, including its Google Ads attribution.
export const buildReminderLeadDetails = (upcomingLeadInfo, phone) => {
  const { fbc, fbclid, googleAds, testMode, responses, leadZip } =
    upcomingLeadInfo;
  return {
    fbclid,
    fbc,
    googleAds,
    leadZip,
    testMode,
    responses,
    formData: {
      name: upcomingLeadInfo.leadName,
      email: upcomingLeadInfo.leadEmail,
      phone
    }
  };
};

// Shown instead of the raw (English) request error, e.g. "Network Error"
const SUBMIT_ERROR_MESSAGE =
  "Ihre Anfrage konnte leider nicht gesendet werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.";

export const useLeadAction = () => {
  const dispatch = useDispatch();
  const zipInfo = useSelector((state) => state.zipInfo);
  const funnelInfo = useSelector((state) => state.funnelInfo);
  const reclaimMode = useSelector((state) => state.reclaimMode);

  const invalidNumberMsg = () => (
    <SuggestionText className="mt-1">
      <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
      <span>Keine gültige Nummer</span>
    </SuggestionText>
  );

  const checkPhoneNumberValidity = ({ phoneNumber, numberCheck, setError }) => {
    // testing required!
    const isValid = isValidPhoneNumber(phoneNumber, numberCheck);
    if (!isValid) {
      setError("phone", { type: "custom", message: invalidNumberMsg() });
      return false;
    }
    return true;
  };

  const handleSubmitLeadInfo = async ({
    formValues,
    setLeadError,
    setIsLoading,
    upcomingLeadDetails = null,
    amPrivacyPolicy
  }) => {
    setIsLoading(true);
    try {
      let submitFormValues = funnelInfo.leadForm.isLandedFromPhoneReminderEmail
        ? upcomingLeadDetails?.formData
        : formValues;
      const splittedName = submitFormValues.name.split(" ");
      const firstName = splittedName[0];
      // remove whitespaces except actual ones
      const lastName =
        removeWhiteSpaces({ string: splittedName.slice(1).join(" ") }) || "";
      dispatch(
        updateUrlParams({
          email: submitFormValues.email,
          firstName,
          lastName,
          phone: normalizeNumber(submitFormValues.phone)
        })
      );
      const serachParams = queryString.parse(window.location.search);
      const fbClId = upcomingLeadDetails?.fbclid || serachParams.fbclid;
      const fbc = upcomingLeadDetails?.fbc || localStorage.getItem("fbc");
      // Use TikTok click id from upcoming lead, URL, or saved storage.
      const ttclid =
        upcomingLeadDetails?.ttclid ||
        serachParams.ttclid ||
        localStorage.getItem("ttclid");
      const dynamicSource = serachParams.source?.toLowerCase();
      const testMode =
        upcomingLeadDetails?.testMode || serachParams.testMode === "true"
          ? true
          : false;
      const { funnel } = funnelInfo;
      const { googleAds, isGoogleVisit } = getLeadGoogleAds({
        upcomingGoogleAds: upcomingLeadDetails?.googleAds
      });
      const leadPayload = {
        clickId: fbClId,
        fbc,
        ttclid,
        testMode,
        lead: {
          funnelId: funnel._id,
          leadDetails: {
            leadName: submitFormValues.name,
            leadEmail: submitFormValues.email,
            leadPhone: normalizeNumber(submitFormValues.phone),
            leadZip: upcomingLeadDetails?.leadZip || zipInfo.value,
            leadType: upcomingLeadDetails?.leadType
          },
          // am_* values of a Google Ads visit are no Meta ad details
          ...(isGoogleVisit
            ? {}
            : { adDetails: metaAdDetailsFromParams(serachParams) }),
          ...(googleAds ? { googleAds } : {}),
          responses: upcomingLeadDetails?.responses || funnelInfo.qResponses,
          source: dynamicSource,
          sourceUrl: window.location.href,
          policyVersion: generatePolicyVersion({
            funnel,
            reclaimMode,
            amPrivacyPolicy
          }),
          submittedAt: new Date().toISOString(),
          reclaimed: reclaimMode.enabled,
          // Split test: which order-form variant this visitor saw (if any).
          // senderId lets the backend verify the assignment against the
          // visitor's hash and the recorded exposure before counting the lead.
          ...(funnelInfo.splitTest?.experimentId &&
          !testMode &&
          localStorage.getItem("senderId")
            ? {
                splitTest: {
                  experimentId: funnelInfo.splitTest.experimentId,
                  variant: funnelInfo.splitTest.variant,
                  senderId: localStorage.getItem("senderId")
                }
              }
            : {})
        }
      };
      const response = await saveLeadResponse(leadPayload);
      const leadId = response.data.data._id;
      // Fire TikTok browser conversion only when the selected pixel is TikTok.
      trackTikTokSubmitForm(leadId, funnel);

      // KI-Interview (letzter Schritt vor der Danke-Seite, für jeden Funnel):
      // App.js lauscht auf dieses Event und legt den Dialog als Overlay
      // über die Danke-Seite, bis der Nutzer fertig ist oder überspringt.
      // KI-Interview: hier nur die Daten hinterlegen — das Event feuert erst
      // nach abgeschlossener OTP-Verifizierung (ThanksDigitalPage), damit das
      // Interview den OTP-Flow nicht überlagert.
      if (funnel.customer?.isAiInterviewAndDossierEnabled) {
        window.__aiInterviewLeadId = leadId;
        // Bootstrap für lokale Entwicklung (Lead liegt ggf. in anderer DB):
        // das Backend nutzt es nur, wenn AI_INTERVIEW_ALLOW_BOOTSTRAP gesetzt ist.
        window.__aiInterviewBootstrap = {
          responses: leadPayload.lead.responses
        };
      } else {
        delete window.__aiInterviewLeadId;
        delete window.__aiInterviewBootstrap;
      }

      // Manually send DataLayer event after server success to inform GTM about lead conversion
      const { leadEmail, leadPhone } = leadPayload.lead.leadDetails;
      window.dataLayer = window.dataLayer || [];
      // Prevent double firing
      if (!window.__lpLeadSent) {
        window.__lpLeadSent = true;
        window.dataLayer.push({
          event: "lp_lead_submitted",
          user_data: {
            email: (leadEmail || "").trim().toLowerCase(),
            phone_number: leadPhone // preferably in E.164 format (+491701234567)
          },
          // Google Ads click data for the customer GTM containers
          tracking_data: buildTrackingData(
            googleAds,
            leadPayload.lead.submittedAt
          )
        });
      }

      dispatch(
        updateRecordActionState({
          insightRefId: INSIGHTS_TYPE.LEAD,
          insightType: INSIGHTS_TYPE.LEAD,
          nextInsightRefId: INSIGHTS_TYPE.LEAD_CREATED,
          nextInsightType: INSIGHTS_TYPE.LEAD_CREATED,
          leadId
        })
      );
      // after save lead response check if there is an upcoming lead(creating a lead with 2-steps enabled), then remove it
      if (funnelInfo.leadForm.leadId) {
        try {
          await softDeleteUpcomingLeadInfo(funnelInfo.leadForm.leadId);
        } catch (error) {
          console.log("Error in deleting upcoming lead info", error);
        }
      }
      if (funnelInfo.leadForm.leadId !== leadId) {
        dispatch(updateLeadId(leadId));
      }
    } catch (error) {
      console.log("Error in saving lead", error);
      setLeadError(SUBMIT_ERROR_MESSAGE);
    } finally {
      // setTimeout for display loader
      setTimeout(() => {
        setIsLoading(false);
        dispatch(updateIsFormSubmitted(!funnelInfo.leadForm.isSubmitted));
        dispatch(updateReminderEmailFlag(false));
      }, 3000);
    }
  };

  const handleSubmitUpcomingLeadInfo = async ({
    formValues,
    setDisplayNext,
    setIsSubmitting,
    submissionError
  }) => {
    setIsSubmitting(true);
    try {
      const serachParams = queryString.parse(window.location.search);
      const fbClId = serachParams.fbclid || null;
      const fbc = localStorage.getItem("fbc");
      // Keep TikTok click id on the upcoming lead for two-step funnels.
      const ttclid = serachParams.ttclid || localStorage.getItem("ttclid");
      const testMode = serachParams.testMode === "true" ? true : false;
      const { funnel, qResponses } = funnelInfo;
      const { googleAds, isGoogleVisit } = getLeadGoogleAds();
      const leadPayload = {
        funnelId: funnel._id,
        funnelVersion: funnel.activeVersion,
        funnelCustomer: funnel.customer._id,
        funnelCategory: funnel.category,
        leadName: formValues.name,
        leadEmail: formValues.email,
        leadZip: zipInfo.value,
        leadType: funnel.general.funnelType,
        // am_* values of a Google Ads visit are no Meta ad details
        ...(isGoogleVisit
          ? {}
          : { adDetails: metaAdDetailsFromParams(serachParams) }),
        ...(googleAds ? { googleAds } : {}),
        testMode,
        sourceUrl: window.location.href,
        submittedAt: new Date().toISOString(),
        policyVersion: undefined,
        fbclid: fbClId,
        fbc,
        ttclid,
        responses: qResponses
      };
      const response = await saveUpcomingLeadInfo(leadPayload);
      const upcomingLeadId = response.data.data._id;
      dispatch(
        updateRecordActionState({
          insightRefId: INSIGHTS_TYPE.LEAD_STEP_ONE,
          insightType: INSIGHTS_TYPE.LEAD_STEP_ONE,
          nextInsightRefId: INSIGHTS_TYPE.LEAD,
          nextInsightType: INSIGHTS_TYPE.LEAD
        })
      );
      dispatch(updateLeadId(upcomingLeadId));
      setTimeout(() => {
        setDisplayNext(true);
        setIsSubmitting(false);
      }, 3000);
    } catch (error) {
      console.log("Error: ", error);
      setTimeout(() => {
        submissionError(SUBMIT_ERROR_MESSAGE);
        setIsSubmitting(false);
      }, 3000);
    }
  };

  return {
    handleSubmitLeadInfo,
    checkPhoneNumberValidity,
    handleSubmitUpcomingLeadInfo
  };
};
